import type { LoaderFunction, MetaFunction } from "@remix-run/cloudflare";
import { useContext, useEffect } from "react";
import { cn } from "@heroui/react";
import { Link, useLoaderData, useNavigate } from "@remix-run/react";
import { HomeScreenTopBar } from "~/components/HomeScreenTopBar";
import { AppContext } from "~/context/AppContext";
import { SignedIn, SignedOut, useUser } from "@clerk/remix";
import { FaDiscord } from "react-icons/fa";
import posthog from "posthog-js";
import { SkillLevelDashboard } from "~/components/skills/SkillLevelDashboard";
import { getAuth } from "@clerk/remix/ssr.server";
import { createClient } from "@supabase/supabase-js";
import { Database } from "~/types/supabase";
import { PrimaryButton } from "~/components/PrimaryButton";
import { Hero, type TrendingScenario } from "~/components/landing-page/Hero";
import type { SocialProofStats } from "~/components/landing-page/SocialProofStrip";
import { FAQS } from "~/data/faqs";
import { jsonLd, ogImageMeta } from "~/utils/seo";

const OG_IMAGE_SOURCE =
  "https://cdn.manupgame.com/create-own-scenario-night-club-halloween.png";
const SITE_URL = "https://manupgame.com/";
const SITE_TITLE =
  "ManUp — Train your wit, frame, and comebacks against AI";
const SITE_DESCRIPTION =
  "Stop freezing when she tests you. Train your reflex against 100+ AI personas in 5 min/day. Free to start, no card required.";

export const meta: MetaFunction = () => {
  return [
    { title: SITE_TITLE },
    { name: "description", content: SITE_DESCRIPTION },
    { property: "og:type", content: "website" },
    { property: "og:title", content: SITE_TITLE },
    { property: "og:description", content: SITE_DESCRIPTION },
    { property: "og:url", content: SITE_URL },
    { property: "og:site_name", content: "ManUp" },
    ...ogImageMeta(OG_IMAGE_SOURCE),
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: SITE_TITLE },
    { name: "twitter:description", content: SITE_DESCRIPTION },
    { tagName: "link", rel: "canonical", href: SITE_URL },
    jsonLd({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${SITE_URL}#organization`,
          name: "ManUp",
          url: SITE_URL,
          logo: "https://cdn.manupgame.com/icon.svg",
          sameAs: ["https://discord.gg/h3usDWEwY8"],
        },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}#website`,
          name: "ManUp",
          url: SITE_URL,
          description: SITE_DESCRIPTION,
          publisher: { "@id": `${SITE_URL}#organization` },
        },
        {
          "@type": "SoftwareApplication",
          "@id": `${SITE_URL}#app`,
          name: "ManUp",
          alternateName: "ManUp Game",
          url: SITE_URL,
          description: SITE_DESCRIPTION,
          applicationCategory: "GameApplication",
          applicationSubCategory: "EducationalGame",
          operatingSystem: "Web, iOS, Android",
          inLanguage: "en",
          author: { "@id": `${SITE_URL}#organization` },
          publisher: { "@id": `${SITE_URL}#organization` },
          featureList: [
            "AI-scored shit test training",
            "Interactive AI dating scenarios",
            "Community-built scenario marketplace",
            "Skill tracking across Frame, Humor, Flirt, and Signals",
            "Global leaderboard",
          ],
          offers: [
            {
              "@type": "Offer",
              name: "Free",
              price: "0",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "ManUp PRO (weekly)",
              price: "5.99",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
            },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        },
      ],
    }),
  ];
};

export const loader: LoaderFunction = async (args) => {
  const supabaseUrl = args.context.cloudflare.env.SUPABASE_URL;
  const supabaseKey = args.context.cloudflare.env.SUPABASE_KEY;

  if (!supabaseUrl || !supabaseKey) {
    console.log("Missing environment variables");
    throw new Error("Missing environment variables");
  }
  const supabase = createClient<Database>(supabaseUrl, supabaseKey);

  const { userId } = await getAuth(args);
  if (userId) {
    const { data } = await supabase
      .from("users")
      .select("*")
      .eq("user_id", userId)
      .single();
    if (!data) {
      await supabase.from("users").upsert({ user_id: userId });
      const { data: skills } = await supabase.from("skills").select("*");
      if (skills && skills.length > 0) {
        await supabase.from("user_skills").upsert(
          skills.map((skill) => ({
            user_id: userId,
            skill_id: skill.id,
          }))
        );
      }
    }
    return {
      trendingScenarios: [] as TrendingScenario[],
      stats: {
        totalPlayers: 0,
        totalScenarios: 0,
        totalPlays: 0,
      } satisfies SocialProofStats,
    };
  }

  // Logged-out: surface trending public scenarios + community stats as social proof.
  const [trendingResult, playersResult, scenariosResult, playsResult] =
    await Promise.all([
      supabase
        .from("scenarios")
        .select(
          "id, name, slug, image_url, image_position, difficulty, play_count, creator_handle, owned_by"
        )
        .eq("is_public", true)
        .order("play_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(3),
      supabase
        .from("users")
        .select("user_id", { count: "estimated", head: true }),
      supabase
        .from("scenarios")
        .select("id", { count: "exact", head: true })
        .eq("is_public", true),
      // `estimated` reads from the planner statistics rather than doing
      // a sequential count — keeps this loader fast as game_sessions
      // grows. Numbers near zero may be off by ±10%; not material for
      // the social-proof strip.
      supabase
        .from("game_sessions")
        .select("id", { count: "estimated", head: true }),
    ]);

  const stats: SocialProofStats = {
    totalPlayers: playersResult.count ?? 0,
    totalScenarios: scenariosResult.count ?? 0,
    totalPlays: playsResult.count ?? 0,
  };

  return {
    trendingScenarios: (trendingResult.data ?? []) as TrendingScenario[],
    stats,
  };
};

export default function Index() {
  const { gameData } = useContext(AppContext);
  const { isSignedIn, user } = useUser();
  const { trendingScenarios, stats } = useLoaderData<{
    trendingScenarios: TrendingScenario[];
    stats: SocialProofStats;
  }>();

  const navigate = useNavigate();

  useEffect(() => {
    if (user && isSignedIn) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
      });
    }
  }, [user, isSignedIn]);

  const practiceSkill = async () => {
    navigate("/train");
  };

  return (
    <>
      <SignedIn>
        <div
          className={cn(
            "flex flex-col items-center justify-center h-dvh px-4 relative w-full"
          )}
        >
          <div className="absolute top-0 left-0 right-0">
            <HomeScreenTopBar />
          </div>

          <SkillLevelDashboard
            skills={
              gameData?.skills.filter((s) => s.name !== "Dominance") ?? []
            }
            practiceSkill={practiceSkill}
          />

          <div className="mt-10">
            <PrimaryButton
              onClick={() => {
                navigate("/scenarios");
              }}
            >
              Start Game
            </PrimaryButton>

            <PrimaryButton
              onClick={async () => {
                navigate("/train");
              }}
              baseClassName="mt-4 w-full"
              variant="secondary"
            >
              Train
            </PrimaryButton>
          </div>
          {/* discord button */}
          <div className="flex flex-row justify-center items-center gap-4 mt-4 opacity-60 mb-10">
            <Link
              to="https://discord.gg/h3usDWEwY8"
              target="_blank"
              rel="noreferrer"
            >
              <FaDiscord className="w-8 h-8" />
            </Link>
          </div>
        </div>
      </SignedIn>
      <SignedOut>
        <Hero
          trendingScenarios={trendingScenarios ?? []}
          stats={
            stats ?? {
              totalPlayers: 0,
              totalScenarios: 0,
              totalPlays: 0,
            }
          }
        />
      </SignedOut>
    </>
  );
}
